import React, { useState, useEffect } from "react";
import axios from "axios";
import { Percent, Zap, CheckCircle, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Хук: загружает ставки комиссии один раз ────────────────────────────────
function useCommissionRates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/commission/rates`)
      .then((r) => setData(r.data))
      .catch(() => {}) // некритично
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

// ─── Форматирование даты акции ───────────────────────────────────────────────
function formatDate(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── ActivePromotionBanner ───────────────────────────────────────────────────
// Показывается в Dashboard и TaskList, только когда есть активные акции
export const ActivePromotionBanner = ({ promotions, serviceRates }) => {
  if (!promotions || promotions.length === 0) return null;
  const promo = promotions[0];
  const serviceLabels = promo.service_types
    .map((st) => serviceRates?.[st]?.label || st)
    .join(", ");

  return (
    <div
      data-testid="active-promo-banner"
      className="relative rounded-xl overflow-hidden"
      style={{ background: "var(--rv-navy)", color: "white" }}
    >
      {/* Фоновый декор */}
      <div
        style={{
          position: "absolute",
          top: "-40px",
          right: "-40px",
          width: "160px",
          height: "160px",
          borderRadius: "50%",
          background: "rgba(13,148,136,0.15)",
          pointerEvents: "none",
        }}
      />
      <div className="relative p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: "rgba(13,148,136,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                animation: "promoPulse 2s ease-in-out infinite",
              }}
            >
              <Zap size={18} style={{ color: "var(--rv-teal)" }} />
            </div>
            <div>
              <p
                className="text-sm font-bold"
                style={{ color: "var(--rv-teal)", fontFamily: "Manrope,sans-serif", marginBottom: "2px" }}
              >
                Акция: {promo.name}
              </p>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.75)", fontFamily: "IBM Plex Sans,sans-serif" }}>
                Сниженная комиссия на: {serviceLabels}
              </p>
            </div>
          </div>
          {promo.valid_until && (
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.45)", fontFamily: "IBM Plex Sans,sans-serif" }}>
                До
              </p>
              <p
                data-testid="promo-valid-until"
                className="text-xs font-semibold"
                style={{ color: "rgba(255,255,255,0.85)", fontFamily: "Manrope,sans-serif" }}
              >
                {formatDate(promo.valid_until)}
              </p>
            </div>
          )}
        </div>

        {/* Ставки по акции */}
        {Object.keys(promo.rates_pct || {}).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(promo.rates_pct).map(([st, pct]) => {
              const base = serviceRates?.[st]?.base_rate_pct;
              return (
                <div
                  key={st}
                  data-testid={`promo-rate-${st}`}
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(13,148,136,0.4)",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.6)", fontFamily: "IBM Plex Sans,sans-serif" }}>
                    {serviceRates?.[st]?.label || st}
                  </span>
                  {base && (
                    <span
                      className="text-xs"
                      style={{
                        color: "rgba(255,255,255,0.35)",
                        fontFamily: "IBM Plex Sans,sans-serif",
                        textDecoration: "line-through",
                      }}
                    >
                      {base}%
                    </span>
                  )}
                  <span
                    className="text-sm font-bold tabular-nums"
                    style={{ color: "var(--rv-teal)", fontFamily: "Manrope,sans-serif" }}
                  >
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <style>{`
        @keyframes promoPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
    </div>
  );
};

// ─── PersonalRateCard ────────────────────────────────────────────────────────
// Компактная карточка со ставками для Dashboard (только исполнители)
const PersonalRateCard = ({ rates }) => {
  const { service_rates, verified_executor_discount_pct, is_verified_executor } = rates;

  return (
    <div
      data-testid="personal-rate-card"
      className="reversia-card p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <Percent size={16} style={{ color: "var(--rv-teal)", flexShrink: 0 }} />
        <h3
          className="text-sm font-semibold"
          style={{ fontFamily: "Manrope,sans-serif", color: "var(--rv-text-primary)" }}
        >
          Комиссия платформы
        </h3>
        {is_verified_executor && (
          <span
            data-testid="verify-discount-badge"
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: "rgba(13,148,136,0.1)",
              color: "var(--rv-teal)",
              borderRadius: "20px",
              padding: "2px 8px",
              fontSize: "0.7rem",
              fontWeight: "600",
              fontFamily: "IBM Plex Sans,sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            <CheckCircle size={11} />
            -{verified_executor_discount_pct}% бейдж
          </span>
        )}
      </div>

      <div className="space-y-2">
        {Object.entries(service_rates).map(([st, info]) => {
          const hasDiscount = info.effective_rate_pct < info.base_rate_pct;
          return (
            <div
              key={st}
              data-testid={`rate-row-${st}`}
              className="flex items-center justify-between py-2"
              style={{ borderBottom: "1px solid var(--rv-border)" }}
            >
              <span
                className="text-sm"
                style={{ color: "var(--rv-text-secondary)", fontFamily: "IBM Plex Sans,sans-serif" }}
              >
                {info.label}
              </span>
              <div className="flex items-center gap-2">
                {hasDiscount && (
                  <span
                    className="text-xs tabular-nums"
                    style={{
                      color: "var(--rv-text-muted)",
                      fontFamily: "IBM Plex Sans,sans-serif",
                      textDecoration: "line-through",
                    }}
                  >
                    {info.base_rate_pct}%
                  </span>
                )}
                <span
                  className="text-sm font-bold tabular-nums"
                  style={{
                    color: hasDiscount ? "var(--rv-teal)" : "var(--rv-text-primary)",
                    fontFamily: "Manrope,sans-serif",
                  }}
                >
                  {info.effective_rate_pct}%
                </span>
                {info.promotion_active && (
                  <span
                    style={{
                      display: "inline-block",
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "var(--rv-teal)",
                      flexShrink: 0,
                    }}
                    title="Акционная ставка"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {is_verified_executor && (
        <p className="text-xs mt-3" style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}>
          Скидка -{verified_executor_discount_pct}% применена к вашим ставкам как проверенному исполнителю.
        </p>
      )}
    </div>
  );
};

// ─── CommissionDashboardSection ──────────────────────────────────────────────
// Секция для Dashboard: баннер акции + таблица ставок
export const CommissionDashboardSection = () => {
  const { data, loading } = useCommissionRates();
  if (loading || !data) return null;

  return (
    <div className="space-y-3">
      {data.has_active_promotions && (
        <ActivePromotionBanner
          promotions={data.active_promotions}
          serviceRates={data.service_rates}
        />
      )}
      <PersonalRateCard rates={data} />
    </div>
  );
};

// ─── CommissionPromoBannerSlim ───────────────────────────────────────────────
// Тонкий баннер для TaskList — только когда есть акция
export const CommissionPromoBannerSlim = () => {
  const { data, loading } = useCommissionRates();
  if (loading || !data || !data.has_active_promotions) return null;

  const promo = data.active_promotions[0];
  const serviceLabels = promo.service_types
    .map((st) => data.service_rates?.[st]?.label || st)
    .join(", ");

  return (
    <div
      data-testid="promo-banner-slim"
      className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl"
      style={{ background: "var(--rv-navy)", color: "white" }}
    >
      <div className="flex items-center gap-2">
        <Zap size={14} style={{ color: "var(--rv-teal)", flexShrink: 0 }} />
        <span className="text-sm font-medium" style={{ fontFamily: "IBM Plex Sans,sans-serif" }}>
          <span style={{ color: "var(--rv-teal)", fontWeight: "700" }}>Акция:</span>{" "}
          сниженная комиссия на {serviceLabels}
        </span>
      </div>
      {promo.valid_until && (
        <span
          className="text-xs"
          style={{ color: "rgba(255,255,255,0.5)", fontFamily: "IBM Plex Sans,sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          до {formatDate(promo.valid_until)}
        </span>
      )}
    </div>
  );
};

// ─── CommissionRatesBlock ────────────────────────────────────────────────────
// Детальная таблица для ProfileSettings (исполнитель)
export const CommissionRatesBlock = () => {
  const { data, loading } = useCommissionRates();

  if (loading) {
    return (
      <div className="reversia-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Percent size={16} style={{ color: "var(--rv-teal)" }} />
          <h3 className="text-base font-semibold" style={{ fontFamily: "Manrope,sans-serif", color: "var(--rv-text-primary)" }}>
            Ставки комиссии
          </h3>
        </div>
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 mx-auto" style={{ borderColor: "var(--rv-teal)" }} />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { service_rates, verified_executor_discount_pct, is_verified_executor, active_promotions, has_active_promotions } = data;

  return (
    <div data-testid="commission-rates-table" className="reversia-card p-6">
      <div className="flex items-center gap-2 mb-5">
        <Percent size={16} style={{ color: "var(--rv-teal)" }} />
        <h3
          className="text-base font-semibold"
          style={{ fontFamily: "Manrope,sans-serif", color: "var(--rv-text-primary)" }}
        >
          Ставки комиссии платформы
        </h3>
      </div>

      {/* Акция, если есть */}
      {has_active_promotions && (
        <div
          className="mb-5 rounded-lg px-4 py-3 flex items-center gap-3"
          style={{ background: "rgba(13,148,136,0.08)", border: "1px solid rgba(13,148,136,0.2)" }}
        >
          <Zap size={14} style={{ color: "var(--rv-teal)", flexShrink: 0 }} />
          <p className="text-sm" style={{ color: "var(--rv-teal)", fontFamily: "IBM Plex Sans,sans-serif" }}>
            <strong>Акция активна:</strong> {active_promotions[0].name}
            {active_promotions[0].valid_until && (
              <> — до {formatDate(active_promotions[0].valid_until)}</>
            )}
          </p>
        </div>
      )}

      {/* Таблица ставок */}
      <div className="space-y-0">
        {/* Заголовок */}
        <div
          className="grid grid-cols-3 gap-2 pb-2 mb-1"
          style={{ borderBottom: "2px solid var(--rv-border)" }}
        >
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}>
            Тип услуги
          </span>
          <span className="text-xs font-semibold uppercase tracking-wide text-right" style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}>
            Базовая
          </span>
          <span className="text-xs font-semibold uppercase tracking-wide text-right" style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}>
            Ваша ставка
          </span>
        </div>

        {Object.entries(service_rates).map(([st, info]) => {
          const hasDiscount = info.effective_rate_pct < info.base_rate_pct;
          return (
            <div
              key={st}
              data-testid={`profile-rate-row-${st}`}
              className="grid grid-cols-3 gap-2 py-3"
              style={{ borderBottom: "1px solid var(--rv-border)" }}
            >
              <span
                className="text-sm"
                style={{ color: "var(--rv-text-secondary)", fontFamily: "IBM Plex Sans,sans-serif" }}
              >
                {info.label}
              </span>
              <span
                className="text-sm tabular-nums text-right"
                style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}
              >
                {info.base_rate_pct}%
              </span>
              <span
                className="text-sm font-bold tabular-nums text-right"
                style={{
                  color: hasDiscount ? "var(--rv-teal)" : "var(--rv-text-primary)",
                  fontFamily: "Manrope,sans-serif",
                }}
              >
                {info.effective_rate_pct}%
                {info.promotion_active && (
                  <span
                    style={{
                      display: "inline-block",
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      background: "var(--rv-teal)",
                      marginLeft: "4px",
                      verticalAlign: "middle",
                    }}
                  />
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Скидка для верифицированных */}
      <div
        className="mt-4 flex items-start gap-3 p-4 rounded-lg"
        style={{ background: is_verified_executor ? "rgba(13,148,136,0.07)" : "var(--rv-paper)", border: "1px solid var(--rv-border)" }}
      >
        <CheckCircle
          size={16}
          style={{ color: is_verified_executor ? "var(--rv-teal)" : "var(--rv-text-muted)", flexShrink: 0, marginTop: "1px" }}
        />
        <div>
          <p
            className="text-sm font-semibold"
            style={{ color: is_verified_executor ? "var(--rv-teal)" : "var(--rv-text-secondary)", fontFamily: "Manrope,sans-serif" }}
          >
            {is_verified_executor
              ? `Скидка проверенного исполнителя применена: -${verified_executor_discount_pct}%`
              : `Скидка для проверенных исполнителей: -${verified_executor_discount_pct}%`}
          </p>
          <p className="text-xs mt-0.5" style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}>
            {is_verified_executor
              ? "Зелёный бейдж активен — ваши ставки уменьшены."
              : "Пройдите верификацию, чтобы получить сниженную ставку на все услуги."}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── CustomerTransparencyNote ────────────────────────────────────────────────
// Для ProfileSettings заказчика
export const CustomerTransparencyNote = () => {
  return (
    <div
      data-testid="customer-transparency-note"
      className="reversia-card p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <Info size={16} style={{ color: "var(--rv-teal)" }} />
        <h3
          className="text-base font-semibold"
          style={{ fontFamily: "Manrope,sans-serif", color: "var(--rv-text-primary)" }}
        >
          Прозрачная комиссия
        </h3>
      </div>

      <div
        className="p-4 rounded-lg space-y-2"
        style={{ background: "rgba(13,148,136,0.05)", border: "1px solid rgba(13,148,136,0.15)" }}
      >
        <p className="text-sm" style={{ color: "var(--rv-text-secondary)", fontFamily: "IBM Plex Sans,sans-serif", lineHeight: "1.7" }}>
          Вы платите ровно столько, сколько указали при создании задания.
          Комиссию платформы несёт исполнитель — из суммы его выплаты.
        </p>
        <p className="text-sm" style={{ color: "var(--rv-text-secondary)", fontFamily: "IBM Plex Sans,sans-serif", lineHeight: "1.7" }}>
          Пример: вы разместили задание за <strong>1 000 ₽</strong>.
          Исполнитель получит <strong>900 ₽</strong> (при комиссии 10%).
          С вашего счёта списывается ровно <strong>1 000 ₽</strong>.
        </p>
      </div>
    </div>
  );
};
